import React from 'react';
import { Play, Loader2, Info, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { ConstraintConfig, Manifest, ExternalDependency } from '../datasim.types';

// ─── Constraint definitions ────────────────────────────────────────────────────

interface ConstraintDef {
  key: keyof ConstraintConfig;
  name: string;
  shortName: string;
  description: string;
  impact: string;
  phase: 1 | 2 | 3;
  category: 'universal' | 'structural' | 'behavioral' | 'external' | 'multi-team';
}

const CONSTRAINTS: ConstraintDef[] = [
  {
    key: 'planning_fallacy',
    name: 'Planning Fallacy',
    shortName: 'Planning Fallacy',
    description: 'Human estimates are systematically optimistic. Work described as "3 weeks" typically takes longer — not because anyone is wrong, but because estimates exclude unknown unknowns.',
    impact: 'Inflates most-likely estimates by your chosen factor (default 25%). Applied to all internally-generated estimates.',
    phase: 1,
    category: 'universal',
  },
  {
    key: 'scope_creep',
    name: 'Scope Creep Factor',
    shortName: 'Scope Creep',
    description: 'Scope expands during execution. First-time implementations, regulatory projects, and long-duration engagements are particularly prone.',
    impact: 'Adds a configurable % inflation to total effort. Default 15%. Recommended for projects > 6 months.',
    phase: 1,
    category: 'structural',
  },
  {
    key: 'parkinson',
    name: "Parkinson's Law",
    shortName: "Parkinson's",
    description: "Work expands to fill available time. Early completions rarely cascade — teams use the buffer or find more work to do.",
    impact: 'Right-skews distributions for tasks with fixed end dates. Pessimistic tail increases; optimistic unchanged.',
    phase: 1,
    category: 'behavioral',
  },
  {
    key: 'student_syndrome',
    name: 'Student Syndrome',
    shortName: 'Student Syndrome',
    description: 'Resources delay starting until deadline pressure is felt. Knowledge workers are especially prone on discretionary early work.',
    impact: 'Right-skews individual task distributions. Most-likely and pessimistic shift right; optimistic unchanged.',
    phase: 1,
    category: 'behavioral',
  },
  {
    key: 'external_deps',
    name: 'External Dependency Volatility',
    shortName: 'External Deps',
    description: 'Third parties, regulators, and vendors add fat-tailed risk. Their timelines are outside your control.',
    impact: 'Adds a discrete probability event per dependency: if they are late (per stated probability), a delay is sampled from their conditional distribution.',
    phase: 1,
    category: 'external',
  },
  {
    key: 'key_person',
    name: 'Key Person Dependency',
    shortName: 'Key Person',
    description: 'Single-threaded resources create hidden fragility. If one person is unavailable, the workstream stalls.',
    impact: 'Binary risk event per key person: probability of unavailability × conditional delay distribution.',
    phase: 1,
    category: 'structural',
  },
];

const MULTI_TEAM_CONSTRAINTS = [
  {
    name: "Brooks' Law",
    description: 'Adding people to a late project makes it later. Headcount increases add quadratic communication overhead.',
    phase: 3,
  },
  {
    name: 'Merge Bias',
    description: 'Parallel tracks converging at a point are delayed by the slowest path. Critical path estimates understate true program duration.',
    phase: 3,
  },
];

const CATEGORY_LABELS: Record<string, string> = {
  universal: 'Universal',
  structural: 'Structural',
  behavioral: 'Behavioral',
  external: 'External Dependencies',
  'multi-team': 'Multi-Team (Phase 3)',
};

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  manifest: Manifest | null;
  config: ConstraintConfig;
  onChange: (config: ConstraintConfig) => void;
  onRunSimulation: () => void;
  simulating: boolean;
  completeVarCount: number;
}

export default function DataSimConstraintReview({
  manifest, config, onChange, onRunSimulation, simulating, completeVarCount
}: Props) {
  const [expandedKey, setExpandedKey] = React.useState<string | null>('planning_fallacy');

  function toggle(key: keyof ConstraintConfig) {
    onChange({ ...config, [key]: { ...config[key], enabled: !config[key].enabled } });
  }

  function updateField(key: keyof ConstraintConfig, field: string, value: any) {
    onChange({ ...config, [key]: { ...config[key], [field]: value } });
  }

  const externalDeps: ExternalDependency[] = manifest?.external_dependencies || [];
  const hasExternalDeps = externalDeps.length > 0;

  // Auto-enable external_deps if manifest has external dependencies
  React.useEffect(() => {
    if (hasExternalDeps && !config.external_deps.enabled) {
      onChange({ ...config, external_deps: { enabled: true } });
    }
  }, [hasExternalDeps]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

      {/* ── LEFT: Constraints list ──────────────────────────────────────── */}
      <div className="lg:col-span-2 space-y-3">
        <div>
          <h3 className="text-sm font-bold text-gray-800">Constraint Review</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Review auto-detected constraints and configure each one. The AI analyst flagged these based on your intake responses.
          </p>
        </div>

        {CONSTRAINTS.map(c => {
          const cfg = config[c.key] as any;
          const isEnabled = cfg.enabled;
          const isExpanded = expandedKey === c.key;

          return (
            <div
              key={c.key}
              className={`bg-white border rounded-xl overflow-hidden shadow-sm transition-all ${
                isEnabled ? 'border-red-200' : 'border-gray-200'
              }`}
            >
              {/* Header row */}
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer"
                onClick={() => setExpandedKey(isExpanded ? null : c.key)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Toggle */}
                  <button
                    onClick={e => { e.stopPropagation(); toggle(c.key); }}
                    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
                      isEnabled ? 'bg-red-500' : 'bg-gray-200'
                    }`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      isEnabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold ${isEnabled ? 'text-gray-800' : 'text-gray-400'}`}>
                      {c.name}
                    </p>
                    <p className="text-xs text-gray-400 capitalize">{CATEGORY_LABELS[c.category]}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {c.key === 'planning_fallacy' && isEnabled && (
                    <span className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full font-medium">
                      +{Math.round((config.planning_fallacy.inflation_factor) * 100)}%
                    </span>
                  )}
                  {c.key === 'scope_creep' && isEnabled && (
                    <span className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full font-medium">
                      +{Math.round((config.scope_creep.inflation_pct) * 100)}%
                    </span>
                  )}
                  {c.key === 'external_deps' && hasExternalDeps && (
                    <span className="text-xs bg-orange-50 text-orange-600 border border-orange-200 px-2 py-0.5 rounded-full font-medium">
                      {externalDeps.length} dep{externalDeps.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                  <p className="text-xs text-gray-600 leading-relaxed">{c.description}</p>
                  <div className="flex items-start gap-1.5 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                    <Info className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700 leading-relaxed">{c.impact}</p>
                  </div>

                  {/* Planning Fallacy config */}
                  {c.key === 'planning_fallacy' && isEnabled && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-600">Inflation factor</label>
                        <span className="text-xs font-bold text-red-600">
                          +{Math.round(config.planning_fallacy.inflation_factor * 100)}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="10" max="50" step="5"
                        value={Math.round(config.planning_fallacy.inflation_factor * 100)}
                        onChange={e => updateField('planning_fallacy', 'inflation_factor', parseInt(e.target.value) / 100)}
                        className="w-full accent-red-500"
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>10% (conservative)</span>
                        <span>50% (aggressive)</span>
                      </div>
                    </div>
                  )}

                  {/* Scope Creep config */}
                  {c.key === 'scope_creep' && isEnabled && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-600">Scope inflation</label>
                        <span className="text-xs font-bold text-red-600">
                          +{Math.round(config.scope_creep.inflation_pct * 100)}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="5" max="30" step="5"
                        value={Math.round(config.scope_creep.inflation_pct * 100)}
                        onChange={e => updateField('scope_creep', 'inflation_pct', parseInt(e.target.value) / 100)}
                        className="w-full accent-red-500"
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>5% (minor)</span>
                        <span>30% (high risk)</span>
                      </div>
                    </div>
                  )}

                  {/* External deps list */}
                  {c.key === 'external_deps' && isEnabled && hasExternalDeps && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">Dependencies captured in intake:</p>
                      {externalDeps.map(dep => (
                        <div key={dep.id} className="flex items-center justify-between bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
                          <div>
                            <p className="text-xs font-semibold text-gray-700">{dep.name}</p>
                            {dep.owner && <p className="text-xs text-gray-400">{dep.owner}</p>}
                          </div>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            (dep.probability_on_time ?? 0.7) >= 0.8 ? 'bg-green-100 text-green-700' :
                            (dep.probability_on_time ?? 0.7) >= 0.5 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {Math.round((dep.probability_on_time ?? 0.7) * 100)}% on time
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {c.key === 'external_deps' && !hasExternalDeps && (
                    <p className="text-xs text-gray-400 italic">No external dependencies were captured during intake. Enable this if you have third-party or regulatory dependencies.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Multi-team constraints (locked, Phase 3) */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-2">Multi-Team Constraints (Phase 3)</p>
          {MULTI_TEAM_CONSTRAINTS.map(c => (
            <div key={c.name} className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-2 flex items-center gap-3 opacity-60">
              <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-500">{c.name}</p>
                <p className="text-xs text-gray-400">{c.description}</p>
              </div>
              <span className="ml-auto text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                Phase 3
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT: Summary + Run button ──────────────────────────────────── */}
      <div className="space-y-4">

        {/* Active constraints summary */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Active Constraints</p>
          <div className="space-y-2">
            {CONSTRAINTS.filter(c => (config[c.key] as any).enabled).map(c => (
              <div key={c.key} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">{c.shortName}</span>
              </div>
            ))}
            {CONSTRAINTS.every(c => !(config[c.key] as any).enabled) && (
              <p className="text-sm text-gray-400">No constraints active</p>
            )}
          </div>
        </div>

        {/* Variable count */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Simulation Ready</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${completeVarCount >= 3 ? 'bg-green-500' : 'bg-amber-400'}`} />
              <span className="text-xs text-gray-600">
                {completeVarCount} variable{completeVarCount !== 1 ? 's' : ''} with full ranges
                {completeVarCount < 3 && <span className="text-amber-600"> (min 3 recommended)</span>}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
              <span className="text-xs text-gray-600">10,000 iterations</span>
            </div>
          </div>
        </div>

        {/* Run simulation */}
        <button
          onClick={onRunSimulation}
          disabled={simulating || completeVarCount < 2}
          className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-red-600 text-white rounded-xl font-semibold text-sm hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
        >
          {simulating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Running simulation...</>
          ) : (
            <><Play className="w-4 h-4" /> Next: AI Scenarios &rarr;</>
          )}
        </button>

        {completeVarCount < 2 && (
          <p className="text-xs text-amber-600 text-center">
            Need at least 2 variables with full estimates to simulate.
          </p>
        )}
      </div>
    </div>
  );
}
